import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, IconButton, Tooltip, Chip, Box,
         Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
         TextField, Button, Alert } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LockIcon from '@mui/icons-material/Lock';
import { authApi } from '../api';

const getInitials = (name) => {
  if (!name) return 'CS';
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
};

function NavBar({ auth, onLogout, darkMode, toggleDarkMode, sidebarOpen, toggleSidebar, user }) {
  const brandName = user?.company?.name || process.env.REACT_APP_APP_NAME || 'CPT Swarm';
  const logoUrl = user?.company?.logoUrl;
  const initials = getInitials(brandName);

  const [anchorEl, setAnchorEl] = useState(null);
  const [pwDialog, setPwDialog] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');

  const handleChangePw = async () => {
    setPwError(''); setPwSuccess('');
    if (pwForm.next !== pwForm.confirm) { setPwError('New passwords do not match'); return; }
    if (pwForm.next.length < 6) { setPwError('Password must be at least 6 characters'); return; }
    try {
      await authApi.changePassword(pwForm.current, pwForm.next);
      setPwSuccess('Password changed successfully');
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      setPwError(err.response?.data?.message || 'Failed to change password');
    }
  };

  return (
    <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar>
        {auth && (
          <IconButton color="inherit" onClick={toggleSidebar} edge="start" sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          {logoUrl ? (
            <Box component="img" src={logoUrl} alt={brandName} sx={{ height: 32, mr: 2, borderRadius: 1 }} />
          ) : (
            <Box sx={{
              width: 32, height: 32, mr: 2, borderRadius: 1, bgcolor: 'primary.dark',
              color: 'primary.contrastText', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontWeight: 'bold', fontSize: 14,
              border: '1px solid rgba(255,255,255,0.3)'
            }}>
              {initials}
            </Box>
          )}
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{brandName}</Typography>
        </Box>
        {auth && user && (
          <>
            <Chip
              label={`${user.username} (${user.role})`}
              color="default" variant="outlined" size="small"
              sx={{ mr: 1, color: 'inherit', borderColor: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}
              icon={<AccountCircleIcon style={{ color: 'white' }} />}
              onClick={e => setAnchorEl(e.currentTarget)}
            />
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem onClick={() => { setAnchorEl(null); setPwDialog(true); }}>
                <LockIcon fontSize="small" sx={{ mr: 1 }} /> Change Password
              </MenuItem>
              <MenuItem onClick={() => { setAnchorEl(null); onLogout(); }}>
                <LogoutIcon fontSize="small" sx={{ mr: 1 }} /> Logout
              </MenuItem>
            </Menu>
          </>
        )}
        <Tooltip title={darkMode ? 'Light mode' : 'Dark mode'}>
          <IconButton color="inherit" onClick={toggleDarkMode}>
            {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
        </Tooltip>
        {auth && !user && (
          <Tooltip title="Logout">
            <IconButton color="inherit" onClick={onLogout}><LogoutIcon /></IconButton>
          </Tooltip>
        )}
      </Toolbar>

      <Dialog open={pwDialog} onClose={() => { setPwDialog(false); setPwError(''); setPwSuccess(''); }} maxWidth="xs" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          {pwError && <Alert severity="error" sx={{ mb: 2 }}>{pwError}</Alert>}
          {pwSuccess && <Alert severity="success" sx={{ mb: 2 }}>{pwSuccess}</Alert>}
          <TextField label="Current Password" type="password" fullWidth margin="normal"
            value={pwForm.current} onChange={e => setPwForm(p => ({ ...p, current: e.target.value }))} />
          <TextField label="New Password" type="password" fullWidth margin="normal"
            value={pwForm.next} onChange={e => setPwForm(p => ({ ...p, next: e.target.value }))} />
          <TextField label="Confirm New Password" type="password" fullWidth margin="normal"
            value={pwForm.confirm} onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPwDialog(false); setPwError(''); setPwSuccess(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleChangePw}>Change Password</Button>
        </DialogActions>
      </Dialog>
    </AppBar>
  );
}
export default NavBar;
